import { useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { Icon } from '@/components/atoms/Icon';
import { BottomSheetDialog } from '@/pwa/atoms/BottomSheetDialog/BottomSheetDialog';
import { formatAddress } from '@karehero/helpers/address';
import { formatDate } from '@karehero/helpers/datetime';
import {
  formatRatingLabel,
  formatProviderPrice,
} from '@karehero/helpers/provider';
import {
  CQC_DIMENSIONS,
  CI_DIMENSIONS,
  type ProviderNote,
  type ProviderType,
  type RatingsTypeCqc,
  type RatingsTypeCi,
  type Shortlist,
  ImageType,
  RatingDimension,
  RatingsType,
} from '@karehero/models';
import TextIcon from '@/components/atoms/TextIcon/TextIcon';

// ─── types ───────────────────────────────────────────────────────────────────

export interface AdminProviderDetailProps {
  provider: ProviderType;
  notes: ProviderNote[];
  currentUser: string;
  shortlists?: Shortlist[];
  selectedShortlistID?: string | null;
  onAddToShortlist?: (shortlistId: string) => void;
  onCreateShortlist?: () => void;
  onAddNote: (text: string) => void;
  onSaveNote: (id: string, text: string) => void;
  onDeleteNote: (id: string) => void;
  onBack: () => void;
}

// ─── internal components ─────────────────────────────────────────────────────

const Stat = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography
      variant='caption'
      sx={{
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
    <Typography variant='body1'>{value}</Typography>
  </Box>
);

export const AddToShortlistMenuButton = ({
  shortlists,
  alreadyOnShortlists,
  selectedShortlistID,
  onAdd,
  onCreateNew,
}: {
  shortlists: Shortlist[];
  alreadyOnShortlists?: Shortlist[];
  selectedShortlistID?: string | null;
  onAdd: (shortlistId: string) => void;
  onCreateNew: () => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Button
        variant='outlined'
        size='small'
        color='primary'
        endIcon={<Icon icon='chevron-down' fontSize={12} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        Add to shortlist
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {shortlists.map((s) => (
          <MenuItem
            key={s.id}
            selected={s.id === selectedShortlistID}
            disabled={!!alreadyOnShortlists?.find((a) => a.id === s.id)}
            onClick={() => {
              setAnchorEl(null);
              onAdd(s.id);
            }}
          >
            <Stack direction='row' spacing={2}>
              <TextIcon
                text={s.name}
                showIcon={!!alreadyOnShortlists?.find((a) => a.id === s.id)}
                centerText={true}
              />
            </Stack>
          </MenuItem>
        ))}
        {shortlists.length > 0 && <Divider />}
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onCreateNew();
          }}
        >
          Create new shortlist
        </MenuItem>
      </Menu>
    </>
  );
};

export const ProviderHeading = ({
  provider,
  address,
}: {
  provider: ProviderType;
  address: string;
}) => {
  return (
    <Box>
      <Typography
        variant='h3'
        sx={{
          color: 'primary.main',
        }}
      >
        {provider.name}
      </Typography>
      {address && (
        <Typography
          variant='body1'
          sx={{
            color: 'text.secondary',
          }}
        >
          {address}
        </Typography>
      )}
    </Box>
  );
};

export const ProviderInformation = ({
  provider,
  address,
  rating,
  price,
  shortlistButton,
}: {
  provider: ProviderType;
  address: string;
  rating: string | undefined;
  price: string | undefined;
  shortlistButton?: React.ReactNode;
}) => {
  return (
    <Card variant='outlined'>
      <CardContent>
        <Stack spacing={2}>
          <Stack
            direction='row'
            spacing={2}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <ProviderHeading provider={provider} address={address} />
            {shortlistButton}
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 1.5,
            }}
          >
            {rating && <Stat label='Regulator rating' value={rating} />}
            {price && <Stat label='Price' value={price} />}
            {provider.bedCount != null && (
              <Stat label='Beds' value={String(provider.bedCount)} />
            )}
            <Stat
              label='Availability'
              value={
                provider.availability == null
                  ? 'Unknown'
                  : provider.availability
                    ? 'Available'
                    : 'Unavailable'
              }
            />
            {provider.managerName && (
              <Stat label='Manager' value={provider.managerName} />
            )}
          </Box>

          <Divider />

          {(provider.enquiryPhone || provider.website) && (
            <Stack spacing={0.5}>
              <Typography variant='subtitle2'>Contact</Typography>
              {provider.enquiryPhone && (
                <Link
                  href={`tel:${provider.enquiryPhone.replace(/\s/g, '')}`}
                  variant='body1'
                >
                  {provider.enquiryPhone}
                </Link>
              )}
              {provider.website && (
                <Typography
                  variant='body1'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {provider.website}
                </Typography>
              )}
            </Stack>
          )}

          {provider.careServiceTypes &&
            provider.careServiceTypes.length > 0 && (
              <Stack spacing={1}>
                <Typography variant='subtitle2'>Types of care</Typography>
                <Stack
                  direction='row'
                  spacing={1}
                  useFlexGap
                  sx={{
                    flexWrap: 'wrap',
                  }}
                >
                  {provider.careServiceTypes.map((c) => (
                    <Chip key={c} label={c} size='small' />
                  ))}
                </Stack>
              </Stack>
            )}

          {provider.specialisms && provider.specialisms.length > 0 && (
            <Stack spacing={1}>
              <Typography variant='subtitle2'>Specialisms</Typography>
              <Stack
                direction='row'
                spacing={1}
                useFlexGap
                sx={{
                  flexWrap: 'wrap',
                }}
              >
                {provider.specialisms.map((s) => (
                  <Chip key={s} label={s} size='small' variant='outlined' />
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const NoteItem = ({
  note,
  isOwn,
  onSave,
  onDelete,
}: {
  note: ProviderNote;
  isOwn: boolean;
  onSave: (id: string, text: string) => void;
  onDelete: (id: string) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.text);

  return (
    <Box>
      <Stack
        direction='row'
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='subtitle2'>{note.author}</Typography>
        <Stack
          direction='row'
          spacing={0.5}
          sx={{
            alignItems: 'center',
          }}
        >
          <Typography
            variant='caption'
            sx={{
              color: 'text.secondary',
            }}
          >
            {formatDate(note.createdAt, { omitDay: true, year: '2-digit' })}
          </Typography>
          {isOwn && !editing && (
            <>
              <IconButton size='small' onClick={() => setEditing(true)}>
                <Icon icon='pen' fontSize={12} />
              </IconButton>
              <IconButton size='small' onClick={() => onDelete(note.id)}>
                <Icon icon='trash' fontSize={12} />
              </IconButton>
            </>
          )}
        </Stack>
      </Stack>
      {editing ? (
        <Stack
          spacing={1}
          sx={{
            mt: 0.5,
          }}
        >
          <TextField
            multiline
            minRows={2}
            size='small'
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <Stack direction='row' spacing={1}>
            <Button
              size='small'
              variant='contained'
              onClick={() => {
                onSave(note.id, text);
                setEditing(false);
              }}
            >
              Save
            </Button>
            <Button
              size='small'
              variant='outlined'
              onClick={() => {
                setText(note.text);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
            mt: 0.5,
          }}
        >
          {note.text}
        </Typography>
      )}
    </Box>
  );
};

export const NotesPanel = ({
  notes,
  currentUser,
  onAdd,
  onSave,
  onDelete,
}: {
  notes: ProviderNote[];
  currentUser: string;
  onAdd: (text: string) => void;
  onSave: (id: string, text: string) => void;
  onDelete: (id: string) => void;
}) => {
  const [newNote, setNewNote] = useState('');

  return (
    <Stack
      spacing={1.5}
      sx={{
        height: '100%',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        {notes.length === 0 ? (
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
            }}
          >
            No notes yet.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {notes.map((note) => (
              <NoteItem
                key={note.id}
                note={note}
                isOwn={note.accountId === currentUser}
                onSave={onSave}
                onDelete={onDelete}
              />
            ))}
          </Stack>
        )}
      </Box>
      <Stack spacing={1}>
        <TextField
          multiline
          minRows={2}
          size='small'
          placeholder='Add a note…'
          fullWidth
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
        />
        <Stack
          direction='row'
          sx={{
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant='contained'
            size='small'
            disabled={!newNote.trim()}
            onClick={() => {
              onAdd(newNote.trim());
              setNewNote('');
            }}
          >
            Add
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

export const ProviderNotes = ({
  notes,
  currentUser,
  onAddNote,
  onSaveNote,
  onDeleteNote,
}: {
  notes: ProviderNote[];
  currentUser: string;
  onAddNote: (text: string) => void;
  onSaveNote: (id: string, text: string) => void;
  onDeleteNote: (id: string) => void;
}) => {
  return (
    <Card variant='outlined'>
      <CardContent sx={{ height: '100%' }}>
        <Stack
          spacing={1.5}
          sx={{
            height: '100%',
          }}
        >
          <Typography variant='h4'>Notes</Typography>
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
            }}
          >
            Visible to other CAMs, not the care circle.
          </Typography>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
            }}
          >
            <NotesPanel
              notes={notes}
              currentUser={currentUser}
              onAdd={onAddNote}
              onSave={onSaveNote}
              onDelete={onDeleteNote}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const ProviderNotesDialog = ({
  isOpen,
  onClose,
  providerName,
  notes,
  currentUser,
  onAddNote,
  onSaveNote,
  onDeleteNote,
}: {
  isOpen: boolean;
  onClose: () => void;
  providerName: string;
  notes: ProviderNote[];
  currentUser: string;
  onAddNote: (text: string) => void;
  onSaveNote: (id: string, text: string) => void;
  onDeleteNote: (id: string) => void;
}) => (
  <BottomSheetDialog
    isOpen={isOpen}
    onClose={onClose}
    title={`Notes · ${providerName}`}
  >
    <Stack spacing={1.5}>
      <Typography
        variant='body2'
        sx={{
          color: 'text.secondary',
        }}
      >
        Visible to other CAMs, not the care circle.
      </Typography>
      <NotesPanel
        notes={notes}
        currentUser={currentUser}
        onAdd={onAddNote}
        onSave={onSaveNote}
        onDelete={onDeleteNote}
      />
    </Stack>
  </BottomSheetDialog>
);

export const ProviderPhotos = ({
  providerName,
  photos,
}: {
  providerName: string;
  photos: ImageType[] | undefined;
}) => {
  if (!photos || photos.length === 0) return null;

  return (
    <Box
      sx={{
        gridColumn: '1 / -1',
      }}
    >
      <Card variant='outlined'>
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant='h4'>Photos</Typography>
            <Box
              component='img'
              src={photos[0].large || photos[0].medium}
              alt={providerName}
              sx={{
                width: '100%',
                height: 180,
                borderRadius: '12px',
                objectFit: 'cover',
              }}
            />
            {photos.length > 1 && (
              <Stack direction='row' spacing={1.5}>
                {photos.slice(1, 4).map((photo, i) => (
                  <Box
                    key={i}
                    component='img'
                    src={photo.medium || photo.small}
                    alt={`${providerName} ${i + 2}`}
                    sx={{
                      flex: 1,
                      height: 72,
                      borderRadius: '8px',
                      objectFit: 'cover',
                    }}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export function ProviderOwnerComment({ provider }: { provider: ProviderType }) {
  if (!provider.managerQuote && !provider.description) return null;

  return (
    <Box
      sx={{
        gridColumn: '1 / -1',
      }}
    >
      <Card variant='outlined'>
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant='h4'>From the owner</Typography>
            {provider.managerQuote && (
              <Typography
                variant='body1'
                sx={{
                  fontStyle: 'italic',
                  color: 'text.secondary',
                }}
              >
                {provider.managerQuote}
              </Typography>
            )}
            {provider.description && (
              <Typography
                variant='body1'
                sx={{
                  color: 'text.secondary',
                }}
              >
                {provider.description}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export const ProviderRegulatorRating = ({ rating }: { rating: string }) => {
  return (
    <Card variant='outlined'>
      <CardContent>
        <Stack spacing={1.5}>
          <Typography variant='h4'>Regulator rating</Typography>
          <Chip label={rating} color='success' />
        </Stack>
      </CardContent>
    </Card>
  );
};

export const ProviderInspectionBreakdown = ({
  ratings,
  dimensions,
}: {
  ratings: RatingsType;
  dimensions:
    RatingDimension<RatingsTypeCqc>[] | RatingDimension<RatingsTypeCi>[];
}) => {
  return (
    <Card variant='outlined'>
      <CardContent>
        <Stack spacing={1.5}>
          <Typography variant='h4'>Inspection breakdown</Typography>
          <Stack spacing={1}>
            {dimensions.map(
              ({
                key,
                label,
              }:
                | RatingDimension<RatingsTypeCqc>
                | RatingDimension<RatingsTypeCi>) => {
                const val = ratings[key as keyof RatingsType];
                if (!val) return null;
                return (
                  <Stack
                    key={key as string}
                    direction='row'
                    sx={{
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant='body2'>{label}</Typography>
                    <Chip label={formatRatingLabel(String(val))} size='small' />
                  </Stack>
                );
              },
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

// ─── main ────────────────────────────────────────────────────────────────────

const AdminProviderDetail = ({
  provider,
  notes,
  currentUser,
  shortlists,
  selectedShortlistID,
  onAddToShortlist,
  onCreateShortlist,
  onAddNote,
  onSaveNote,
  onDeleteNote,
  onBack,
}: AdminProviderDetailProps) => {
  console.log(`Shortlists: ${JSON.stringify(shortlists)}`);
  const address = provider.address ? formatAddress(provider.address) : '';
  const rating = provider.ratings?.overallRating
    ? formatRatingLabel(provider.ratings.overallRating)
    : undefined;
  const price = formatProviderPrice(provider);
  const isCqc = provider.regulator === 'cqc';
  const dimensions = isCqc ? CQC_DIMENSIONS : CI_DIMENSIONS;
  const ratings = provider.ratings as RatingsTypeCqc & RatingsTypeCi;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          mb: 2,
        }}
      >
        <Button
          variant='text'
          color='primary'
          startIcon={<Icon icon='chevron-left' fontSize={14} />}
          onClick={onBack}
        >
          Back to results
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          pr: 1,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
          }}
        >
          <ProviderInformation
            provider={provider}
            address={address}
            rating={rating}
            price={price}
            shortlistButton={
              shortlists && onAddToShortlist && onCreateShortlist ? (
                <AddToShortlistMenuButton
                  shortlists={shortlists}
                  selectedShortlistID={selectedShortlistID}
                  alreadyOnShortlists={shortlists.filter((s) =>
                    s.regulatoryIds?.find(
                      (regID) => regID === provider.regulatoryId,
                    ),
                  )}
                  onAdd={onAddToShortlist}
                  onCreateNew={onCreateShortlist}
                />
              ) : undefined
            }
          />

          {/* Notes */}
          <ProviderNotes
            notes={notes}
            currentUser={currentUser}
            onAddNote={onAddNote}
            onSaveNote={onSaveNote}
            onDeleteNote={onDeleteNote}
          />

          {/* Photos */}
          <ProviderPhotos
            providerName={provider.name}
            photos={provider.photos}
          />

          {/* From the owner */}
          <ProviderOwnerComment provider={provider} />

          {/* Regulator rating */}
          {rating && <ProviderRegulatorRating rating={rating} />}

          {/* Inspection breakdown */}
          {ratings && (
            <ProviderInspectionBreakdown
              ratings={ratings}
              dimensions={dimensions}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminProviderDetail;
